import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import { useSelector } from 'react-redux';
import BarChartIcon from '@mui/icons-material/BarChart';
import ComputerIcon from '@mui/icons-material/Computer';
import DivineOrbIcon from '../icons/DivineOrbIcon';
import { selectDivineOrbsPerHour, selectDivineOrbsGained24h } from '../../store/slices/itemsSlice';
import { selectAliveStatuses } from '../../store/slices/charactersSlice';
import type { Character } from '../../store/slices/charactersSlice';

interface CharacterStatsDashboardProps {
  characters: Character[];
}

interface HostInfo {
  hostname: string;
  totalCharacters: number;
  activeCharacters: number;
  inactiveCharacters: number;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, subtitle }) => (
  <Card sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
    <CardContent sx={{ textAlign: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
        {icon}
        <Typography variant='h6' sx={{ ml: 1, fontSize: '0.9rem' }}>
          {title}
        </Typography>
      </Box>
      <Typography variant='h4' component='div' sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.8rem' }}>
        {value === 0 || value === '0.0' || value === '0.00' ? 'N/A' : value}
      </Typography>
      {subtitle && (
        <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.75rem' }}>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const CharacterStatsDashboard: React.FC<CharacterStatsDashboardProps> = ({ characters }) => {
  const [hostsDialogOpen, setHostsDialogOpen] = useState(false);
  const divineOrbsPerHour = useSelector(selectDivineOrbsPerHour);
  const divineOrbsGained24h = useSelector(selectDivineOrbsGained24h);
  const aliveStatuses = useSelector(selectAliveStatuses);

  const stats = useMemo(() => {
    if (characters.length === 0) {
      return {
        avgLevel: 0,
        avgDivs: 0,
        avgDivPerHour: 0,
        avgDivsGained24h: 0,
        totalDivsGained24h: 0,
        avgDeathsPerHour: 0,
      };
    }

    // Calculate average level
    const avgLevel = characters.reduce((sum, char) => sum + char.level, 0) / characters.length;

    // Calculate average divine orbs
    const avgDivs = characters.reduce((sum, char) => sum + (char.divineOrbCount || 0), 0) / characters.length;

    // Calculate average div/hr from characters that have data
    const divPerHourValues = characters
      .map(char => divineOrbsPerHour[char.id]?.divPerHour)
      .filter((value): value is number => value !== undefined && value > 0);
    const avgDivPerHour =
      divPerHourValues.length > 0 ? divPerHourValues.reduce((sum, value) => sum + value, 0) / divPerHourValues.length : 0;

    // Calculate total 24h gains from characters that have data, as well as 24h average
    const divsGained24hValues = characters
      .map(char => divineOrbsGained24h[char.id]?.divineOrbsGained)
      .filter((value): value is number => value !== undefined && value > 0);
    const avgDivsGained24h =
      divsGained24hValues.length > 0 ? divsGained24hValues.reduce((sum, value) => sum + value, 0) / divsGained24hValues.length : 0;
    const sumDivsGained24h = divsGained24hValues.reduce((sum, value) => sum + value, 0);

    // Calculate average deaths per hour
    const deathsPerHourValues = characters
      .map(char => char.deathsPerHour)
      .filter((value): value is number => value !== undefined && value >= 0);
    const avgDeathsPerHour =
      deathsPerHourValues.length > 0 ? deathsPerHourValues.reduce((sum, value) => sum + value, 0) / deathsPerHourValues.length : 0;

    return {
      avgLevel: Math.round(avgLevel * 10) / 10,
      avgDivs: Math.round(avgDivs * 10) / 10,
      avgDivPerHour: Math.round(avgDivPerHour * 100) / 100,
      avgDivsGained24h: Math.round(avgDivsGained24h * 10) / 10,
      avgDeathsPerHour: Math.round(avgDeathsPerHour * 100) / 100,
      totalDivsGained24h: sumDivsGained24h,
    };
  }, [characters, divineOrbsPerHour, divineOrbsGained24h]);

  const hostInfo = useMemo(() => {
    const hostMap = new Map<string, HostInfo>();

    characters.forEach(character => {
      const hostname = character.hostname || 'Unknown';
      const aliveTimestamp = aliveStatuses[character.id];
      const isActive = aliveTimestamp && new Date().getTime() - new Date(aliveTimestamp).getTime() < 5 * 60 * 1000; // Active if alive within 5 minutes

      if (!hostMap.has(hostname)) {
        hostMap.set(hostname, {
          hostname,
          totalCharacters: 0,
          activeCharacters: 0,
          inactiveCharacters: 0,
        });
      }

      const hostData = hostMap.get(hostname)!;
      hostData.totalCharacters++;

      if (isActive) {
        hostData.activeCharacters++;
      } else {
        hostData.inactiveCharacters++;
      }
    });

    return Array.from(hostMap.values()).sort((a, b) => b.totalCharacters - a.totalCharacters);
  }, [characters, aliveStatuses]);

  const handleHostsClick = () => {
    setHostsDialogOpen(true);
  };

  const handleHostsDialogClose = () => {
    setHostsDialogOpen(false);
  };

  if (characters.length === 0) {
    return null;
  }

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant='h6' component='div' sx={{ display: 'flex', alignItems: 'center' }}>
              <BarChartIcon sx={{ mr: 1 }} />
              Character Statistics ({characters.length} characters)
            </Typography>
            <Button variant='outlined' startIcon={<ComputerIcon />} onClick={handleHostsClick} size='small'>
              Hosts ({hostInfo.length})
            </Button>
          </Box>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4, lg: 2 }}>
              <StatCard title='Avg Level' value={stats.avgLevel.toFixed(1)} icon={<Typography variant='h6'>📊</Typography>} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, lg: 2 }}>
              <StatCard title='Avg Divs' value={stats.avgDivs.toFixed(1)} icon={<DivineOrbIcon size={24} />} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, lg: 2 }}>
              <StatCard title='Avg Div/Hr' value={stats.avgDivPerHour.toFixed(2)} icon={<DivineOrbIcon size={24} />} subtitle='per hour' />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, lg: 2 }}>
              <StatCard
                title='Avg 24h Gain'
                value={stats.avgDivsGained24h.toFixed(1)}
                icon={<DivineOrbIcon size={24} />}
                subtitle='last 24 hours'
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, lg: 2 }}>
              <StatCard
                title='Total 24h Gain'
                value={stats.totalDivsGained24h.toFixed(1)}
                icon={<DivineOrbIcon size={24} />}
                subtitle='last 24 hours'
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, lg: 2 }}>
              <StatCard
                title='Avg Deaths/Hr'
                value={stats.avgDeathsPerHour.toFixed(2)}
                icon={<Typography variant='h6'>💀</Typography>}
                subtitle='per hour'
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Hosts Dialog */}
      <Dialog open={hostsDialogOpen} onClose={handleHostsDialogClose} maxWidth='sm' fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ComputerIcon sx={{ mr: 1 }} />
            Host Information
          </Box>
        </DialogTitle>
        <DialogContent>
          <List>
            {hostInfo.map((host, index) => (
              <React.Fragment key={host.hostname}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
                          {host.hostname}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          {host.totalCharacters} total
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Chip label={`${host.activeCharacters} active`} size='small' color='success' variant='outlined' />
                        <Chip label={`${host.inactiveCharacters} inactive`} size='small' color='default' variant='outlined' />
                      </Box>
                    }
                  />
                </ListItem>
                {index < hostInfo.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleHostsDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CharacterStatsDashboard;
